import OverworldStateMachine from '../state-machine/OverworldStateMachine.js';
import { setOSM, getGUI, getOSM } from '../OverworldAnimation.js';
import {
    GAME_VERSION,
    BACKWARDS_COMPATIBILITY_VERSION,
    IDLE,
    LOADING,
    NEW_FILE_STARTED,
    SAVE_FILE_INCOMPATIBLE,
    SET_PLAYER_NAME,
} from '../constants.js';
export default class GameSaveManager {
    constructor() {
        this.saveKey = 'pkmng';
        this.slot = 0;
        this.version = GAME_VERSION;
        this.backwardsCompatibilityVersion = BACKWARDS_COMPATIBILITY_VERSION;
        this.worker = null;
        this._saveInProgress = null;
        this.initWorker();
    }

    initWorker() {
        try {
            // Create Web Worker for async serialization
            // Use a blob URL to create the worker inline (avoids separate file issues)
            const workerCode = `
                self.onmessage = function(e) {
                    const { saveData, saveKey, slot } = e.data;
                    try {
                        const serializedData = JSON.stringify(saveData);
                        self.postMessage({
                            success: true,
                            serializedData: serializedData,
                            saveKey: saveKey,
                            slot: slot
                        });
                    } catch (error) {
                        self.postMessage({
                            success: false,
                            error: error.message
                        });
                    }
                };
            `;
            const blob = new Blob([workerCode], { type: 'application/javascript' });
            this.worker = new Worker(URL.createObjectURL(blob));
        } catch (error) {
            console.warn('Web Worker not supported, falling back to requestIdleCallback:', error);
            this.worker = null;
        }
    }

    setVersion(version) {
        this.version = version;
    }

    async saveGame(overworldStateMachine) {
        // Serialize saves: wait for any in-progress save to finish first,
        // preventing concurrent Worker message handlers from racing.
        if (this._saveInProgress) {
            try {
                await this._saveInProgress;
            } catch (_) {
                // Previous save failed; proceed with this one
            }
        }

        const savePromise = this._performSave(overworldStateMachine);
        this._saveInProgress = savePromise;

        try {
            return await savePromise;
        } finally {
            if (this._saveInProgress === savePromise) {
                this._saveInProgress = null;
            }
        }
    }

    async _performSave(overworldStateMachine) {
        return new Promise((resolve) => {
            const performSave = () => {
                try {
                    // Serialize the game state (this is deferred, but still synchronous once it starts)
                    const saveData = {
                        version: this.version,
                        timestamp: Date.now(),
                        gameState: {
                            overworldStateMachine: overworldStateMachine.serialize(),
                        },
                    };

                    // Use Web Worker for JSON.stringify if available (runs in separate thread)
                    if (this.worker) {
                        const messageHandler = (e) => {
                            this.worker.removeEventListener('message', messageHandler);

                            if (e.data.success) {
                                try {
                                    // Defer localStorage.setItem to avoid blocking
                                    queueMicrotask(() => {
                                        localStorage.setItem(
                                            `${e.data.saveKey}${e.data.slot}`,
                                            e.data.serializedData
                                        );
                                        console.log('Game saved successfully via Web Worker');
                                        resolve({ success: true, message: 'Game saved!' });
                                    });
                                } catch (error) {
                                    console.error('Failed to save to localStorage:', error);
                                    resolve({ success: false, message: `Save failed: ${error.message}` });
                                }
                            } else {
                                console.error('Worker serialization failed:', e.data.error);
                                resolve({ success: false, message: `Save failed: ${e.data.error}` });
                            }
                        };

                        this.worker.addEventListener('message', messageHandler);
                        this.worker.postMessage({
                            saveData: saveData,
                            saveKey: this.saveKey,
                            slot: this.slot,
                        });
                    } else {
                        // Fallback: stringify and save
                        const serializedData = JSON.stringify(saveData);
                        // Defer localStorage.setItem to avoid blocking
                        queueMicrotask(() => {
                            localStorage.setItem(`${this.saveKey}${this.slot}`, serializedData);
                            console.log('Game saved successfully (fallback method)');
                            resolve({ success: true, message: 'Game saved!' });
                        });
                    }
                } catch (error) {
                    console.error('Failed to save game:', error);
                    resolve({ success: false, message: `Save failed: ${error.message}` });
                }
            };

            // Priority order: scheduler.postTask > requestIdleCallback > setTimeout
            // scheduler.postTask with 'background' priority is the best option
            if (typeof scheduler !== 'undefined' && scheduler.postTask) {
                scheduler.postTask(performSave, { priority: 'background' }).catch((error) => {
                    console.error('Scheduler task failed:', error);
                    // Fallback to requestIdleCallback
                    if (typeof requestIdleCallback !== 'undefined') {
                        requestIdleCallback(performSave, { timeout: 5000 });
                    } else {
                        setTimeout(performSave, 0);
                    }
                });
            } else if (typeof requestIdleCallback !== 'undefined') {
                // requestIdleCallback runs when browser is idle (good for non-critical work)
                requestIdleCallback(performSave, { timeout: 5000 });
            } else {
                // Final fallback: use setTimeout to defer to next event loop tick
                setTimeout(performSave, 0);
            }
        });
    }

    // Simple fallback that removes circular references
    fallbackStringify(obj) {
        const seen = new WeakSet();
        let parentKey = null;
        const parentStack = [];
        return JSON.stringify(obj, (key, value) => {
            console.log(`Parent key: ${parentKey}`);
            if (typeof value === 'object' && value !== null) {
                // Skip circular detection for numeric keys (numbers or string numbers)
                const isNumericKey = key === '' || (!isNaN(Number(key)) && key !== '');

                // Skip circular detection for specific object property keys
                const dataKeys = [
                    'statModifier',
                    'statusEffect',
                    'user',
                    'receiver',
                    'battleStages',
                    'stat',
                    'ev',
                    'iv',
                    'stats',
                    'statIncreaseHistory',
                    'increase',
                    'decrease',
                    'total',
                    'moveHistory',
                    'newMoves',
                    'baseStats',
                    'moveSet',
                    'moveSetTMHM',
                    'position',
                    'originalTrainer',
                ];

                const shouldSkipCircularCheck = isNumericKey || dataKeys.includes(key);

                if (shouldSkipCircularCheck) {
                    console.log(`Not checking for circular detection in ${key}`);
                } else {
                    if (!value.increase) {
                        if (seen.has(value)) {
                            console.log(`Circular reference removed at key: "${key}"`);
                            return null;
                        }
                        seen.add(value);
                    }
                }
            }
            parentKey = key;
            parentStack.push(parentKey);
            return value;
        });
    }

    async loadGame() {
        try {
            const saveData = localStorage.getItem(`${this.saveKey}${this.slot}`);
            if (!saveData) {
                getOSM().stateQueue.push(NEW_FILE_STARTED);
                return { success: false, message: 'No save data found' };
            } else {
                console.log(`Save data vailable in slot ${this.slot}...`);
                // strip first 2 characters from version and backwardsCompatibilityVersion
                const version = this.version.slice(2);
                const backwardsCompatibilityVersion = this.backwardsCompatibilityVersion.slice(2);
                if (parseFloat(version) < parseFloat(backwardsCompatibilityVersion)) {
                    console.error(
                        `This save file version (${version}) is not compatible with the most recent backwards compatible version (${backwardsCompatibilityVersion}). The save file will be deleted.`
                    );
                    localStorage.removeItem(`${this.saveKey}${this.slot}`);
                    getOSM().stateQueue.push(SAVE_FILE_INCOMPATIBLE, NEW_FILE_STARTED, SET_PLAYER_NAME);
                    return { success: false, message: 'Save file incompatible' };
                }
            }

            const parsedData = JSON.parse(saveData);
            if (parsedData.version !== this.version) {
                console.warn(`Save version mismatch: ${parsedData.version} vs ${this.version}`);
            }
            const newOSM = await OverworldStateMachine.deserialize(
                parsedData.gameState.overworldStateMachine
            );
            newOSM.stateQueue = [LOADING];

            // Start in LOADING state to prevent auto-save during the transition
            const gui = getGUI();
            setOSM(newOSM);
            gui.refreshOSM();

            console.log('Game loaded successfully');
            newOSM.stateQueue.push(IDLE);

            return {
                success: true,
                message: 'Game loaded!',
                overworldStateMachine: newOSM,
                timestamp: parsedData.timestamp,
            };
        } catch (error) {
            console.error('Failed to load game:', error);
            return { success: false, message: `Load failed: ${error.message}` };
        }
    }

    hasSaveData() {
        return !!localStorage.getItem(this.saveKey);
    }

    deleteSaveData() {
        try {
            localStorage.removeItem(this.saveKey);
            return { success: true, message: 'Save data deleted' };
        } catch (error) {
            console.error('Failed to delete save data:', error);
            return { success: false, message: `Delete failed: ${error.message}` };
        }
    }

    getSaveInfo() {
        try {
            const saveData = localStorage.getItem(this.saveKey);
            if (!saveData) return null;

            const parsedData = JSON.parse(saveData);
            return {
                timestamp: parsedData.timestamp,
                version: parsedData.version,
                playerName: parsedData.gameState.overworldStateMachine.player?.name,
            };
        } catch (error) {
            console.error('Failed to get save info:', error);
            return null;
        }
    }
}

// Export a singleton instance
export const gameSaveManager = new GameSaveManager();
